import * as z from "zod";

// TODO(paul): remove casts
const AddressSchema_ = z.object({
  company: z.string().nullish(),
  name: z.string().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  street1: z.string().nullish(),
  street2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z
    .string()
    .nullish()
    .transform((val) => val ?? ""), // Should probably just be nullish(), but too many type errors to fix right now
  country: z.string().nullish() as unknown as z.ZodString,
  residential: z.boolean().nullish(),
});
export const AddressSchema: z.ZodType<Address> = AddressSchema_;
export interface Address extends z.infer<typeof AddressSchema_> {}

const ShipmentParcelZodSchema_ = z.object({
  length: z.number().nullish(),
  width: z.number().nullish(),
  height: z.number().nullish(),
  weight: z.number(),
});
export const ShipmentParcelZodSchema: z.ZodType<ShipmentParcel> =
  ShipmentParcelZodSchema_;
export interface ShipmentParcel extends z.infer<
  typeof ShipmentParcelZodSchema_
> {}

// TODO(paul): remove casts
const FormSchema_ = z.object({
  id: z.string().nullish() as unknown as z.ZodString,
  form_type: z.string().nullish() as unknown as z.ZodString,
  form_url: z.string().url().nullish() as unknown as z.ZodString,
  submitted_electronically: z.boolean().nullish() as unknown as z.ZodBoolean,
});
const FormSchema: z.ZodType<Form> = FormSchema_;
export interface Form extends z.infer<typeof FormSchema_> {}

export enum ShippingProviderType {
  CANADA_POST = "canada_post",
  EASYPOST = "easypost",
  GLOBALE = "globale",
  IDF_SHIPPING = "idf_shipping",
  MOCK = "mock",
  NZ_POST = "nz_post",
  SEKO = "seko",
  SENDCLOUD = "sendcloud",
  SOAPBOX = "soapbox",
  UPS_DIRECT = "ups_direct",
  USPS_DIRECT = "usps_direct",
  WEGROW = "wegrow",
}

/**
 * Provider types written before a provider's sub-account moved to the
 * `shipping_account` field. These are never produced any more, but persisted
 * shipments and rates still carry them, so every read normalizes through
 * LEGACY_ACCOUNT_PROVIDER_TYPES. Delete both once the backfill has run.
 */
export enum LegacyShippingProviderType {
  SENDCLOUD_EU = "sendcloud_eu",
  UPS_DIRECT_CA = "ups_direct_ca",
  UPS_DIRECT_EU = "ups_direct_eu",
  UPS_DIRECT_US = "ups_direct_us",
}

/**
 * What a `shipping_provider_type` read off a persisted document can hold. A
 * raw document is NOT a `ShippingProviderType` until it goes through
 * `shippingProviderTypeOf`.
 */
export type StoredShippingProviderType =
  | ShippingProviderType
  | LegacyShippingProviderType;

const LEGACY_ACCOUNT_PROVIDER_TYPES: Readonly<
  Record<
    LegacyShippingProviderType,
    { providerType: ShippingProviderType; shippingAccount: string }
  >
> = {
  [LegacyShippingProviderType.SENDCLOUD_EU]: {
    providerType: ShippingProviderType.SENDCLOUD,
    shippingAccount: "EU",
  },
  [LegacyShippingProviderType.UPS_DIRECT_CA]: {
    providerType: ShippingProviderType.UPS_DIRECT,
    shippingAccount: "ca",
  },
  [LegacyShippingProviderType.UPS_DIRECT_EU]: {
    providerType: ShippingProviderType.UPS_DIRECT,
    shippingAccount: "eu",
  },
  [LegacyShippingProviderType.UPS_DIRECT_US]: {
    providerType: ShippingProviderType.UPS_DIRECT,
    shippingAccount: "us",
  },
};

function legacyProviderType(
  value: string,
): { providerType: ShippingProviderType; shippingAccount: string } | undefined {
  return (
    LEGACY_ACCOUNT_PROVIDER_TYPES[value as LegacyShippingProviderType] ??
    undefined
  );
}

/** Every legacy spelling still readable, for backfills and migrations. */
export function legacyShippingProviderTypes(): LegacyShippingProviderType[] {
  return Object.values(LegacyShippingProviderType);
}

function normalizeProviderTypeValue(value: unknown): unknown {
  if (typeof value !== "string") {
    return value;
  }
  return legacyProviderType(value)?.providerType ?? value;
}

/**
 * A `shipping_provider_type` read off any persisted document, normalized
 * through the legacy per-account spellings before the enum check.
 */
export const StoredShippingProviderTypeSchema = z.preprocess(
  normalizeProviderTypeValue,
  z.enum(ShippingProviderType),
);

const shippingProviderTypeSchema = StoredShippingProviderTypeSchema.default(
  ShippingProviderType.EASYPOST,
);

export interface ProviderAccountSource {
  shipping_provider_type?: string | null;
  shipping_account?: string | null;
}

/** Canonical provider type, resolving legacy per-account values. */
export function shippingProviderTypeOf(
  source: ProviderAccountSource | null | undefined,
): ShippingProviderType | undefined {
  const raw = source?.shipping_provider_type;
  if (!raw) {
    return undefined;
  }
  const normalized = legacyProviderType(raw)?.providerType ?? raw;
  return Object.values(ShippingProviderType).includes(
    normalized as ShippingProviderType,
  )
    ? (normalized as ShippingProviderType)
    : undefined;
}

/**
 * Which of the provider's sub-accounts issued this rate or label. The key is
 * opaque and scoped to the provider — only that provider interprets it.
 */
export function shippingAccountOf(
  source: ProviderAccountSource | null | undefined,
): string | undefined {
  if (source?.shipping_account) {
    return source.shipping_account;
  }
  const raw = source?.shipping_provider_type;
  return raw ? legacyProviderType(raw)?.shippingAccount : undefined;
}

export function isShippingProviderType(
  source: ProviderAccountSource | null | undefined,
  providerType: ShippingProviderType,
): boolean {
  return shippingProviderTypeOf(source) === providerType;
}

/**
 * Every stored string that means `providerType`, for Mongo queries that match
 * `shipping_provider_type` directly against persisted documents.
 */
export function shippingProviderTypeQueryValues(
  providerType: ShippingProviderType,
): string[] {
  return [
    providerType,
    ...Object.entries(LEGACY_ACCOUNT_PROVIDER_TYPES)
      .filter(([, legacy]) => legacy.providerType === providerType)
      .map(([value]) => value),
  ];
}

export enum LabelSize {
  FOUR_BY_SIX = "4x6",
  LETTER = "8.5x11",
}

// TODO(paul): remove casts
export const RateSchema_ = z.object({
  id: z.string(),
  shipment_id: z.string(),
  rate: z.union([z.number(), z.string()]).nullish() as unknown as z.ZodString,
  currency: z
    .union([z.number(), z.string()])
    .nullish() as unknown as z.ZodString,
  carrier: z.string(),
  service: z.string(),
  delivery_date: z.string().nullish(),
  delivery_days: z.number().nullish(),
  delivery_date_guaranteed: z.boolean().nullish(),
  upcharge: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  shipping_provider_type: shippingProviderTypeSchema,
  shipping_account: z.string().nullish(),
  carrier_account_id: z.string().nullish() as unknown as z.ZodString,
  is_from_alternative_account: z.boolean().nullish(),
  is_merchant_carrier_account: z.boolean().nullish(),
  sendcloud_contract_id: z.number().nullish(),
  isReturn: z.boolean().optional(),
  options: z
    .object({ labelAvailable: z.boolean(), qrCodeAvailable: z.boolean() })
    .optional(),
});

const RateSchema: z.ZodType<Rate> = RateSchema_;
export interface Rate extends z.infer<typeof RateSchema_> {}

const LabelSchema_ = z.object({
  id: z.string(),
  label_url: z.union([z.string(), z.string().url(), z.null()]),
  label_date: z.any().optional() as unknown as z.ZodString,
});
const LabelSchema: z.ZodType<Label> = LabelSchema_;
export interface Label extends z.infer<typeof LabelSchema_> {}

export interface AddressVerification {
  verifications: {
    delivery: { success: boolean; errors: { message: string }[] };
  };
}

const ShipmentSchema_ = z.object({
  id: z.string(),
  to_address: AddressSchema.nullish(),
  from_address: AddressSchema.nullish(),
  status: z.string().nullish() as unknown as z.ZodString,
  parcel: ShipmentParcelZodSchema.nullish(),
  customs_info: z.any().optional(),
  forms: z.array(FormSchema),
  is_from_alternative_account: z.boolean().nullish(),
  shipping_provider_type: shippingProviderTypeSchema,
  shipping_account: z.string().nullish(),
  rates: z.array(RateSchema),
  selected_rate: RateSchema,
  postage_label: LabelSchema.nullish(),
  labelSize: z.enum(LabelSize).optional(),
  insurance: z.string().nullish() as unknown as z.ZodString,
  tracking_code: z.string(),
  tracker: z.any().optional(), // as z.ZodType<ExternalShipmentTracker>,
  created_at: z.string(),
  updated_at: z.string(),
});
export const ShipmentSchema: z.ZodType<Shipment> = ShipmentSchema_;
export interface Shipment extends z.infer<typeof ShipmentSchema_> {}

export interface TrackingDetail {
  datetime: string;
  message: string;
  tracking_location: {
    city: string;
    state: string;
    country: string;
    zip: string;
  };
}
